import useAuth from '@fuse/core/FuseAuthProvider/useAuth';
import FuseLoading from '@fuse/core/FuseLoading';
import { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router';

/**
 * PaymentCallbackPage handles PayU 3DS redirect after authentication
 * This page is accessible without login and handles the transition back to the authenticated portal
 */
function PaymentCallbackPage() {
    const location = useLocation();
    const navigate = useNavigate();
    const { authState } = useAuth();
    const isAuthenticated = authState?.isAuthenticated;

    useEffect(() => {
        // Get payment result parameters from URL
        const searchParams = new URLSearchParams(location.search);
        const paymentId = searchParams.get('payment_id');
        const status = searchParams.get('status');
        const chargeId = searchParams.get('charge_id');

        // If we have payment parameters, handle them
        if (paymentId && status) {
            if (isAuthenticated) {
                // User is authenticated, redirect to billing with postback=true for consistent 3DS flow
                const chargeParam = chargeId ? `&charge_id=${chargeId}` : '';
                const billingUrl = `/billing/details?postback=true&payment_id=${paymentId}${chargeParam}`;
                navigate(billingUrl, { replace: true });
            } else {
                // User not authenticated, store minimal info and redirect to sign-in
                // After login, they'll be redirected with postback flow
                const paymentResult = {
                    payment_id: paymentId,
                    charge_id: chargeId,
                    timestamp: new Date().getTime()
                };
                localStorage.setItem('payment_callback_result', JSON.stringify(paymentResult));
                navigate(`/sign-in`, { replace: true });
            }
        } else {
            // No payment parameters, check if user is authenticated
            if (isAuthenticated) {
                // Check for stored payment result

                const storedResult = localStorage.getItem('payment_callback_result');

                if (storedResult) {
                    try {
                        const paymentResult = JSON.parse(storedResult);
                        // Clear the stored result
                        localStorage.removeItem('payment_callback_result');
                        // Redirect to billing with postback=true for consistent 3DS flow
                        const chargeParam = paymentResult.charge_id ? `&charge_id=${paymentResult.charge_id}` : '';
                        const billingUrl = `/billing/details?postback=true&payment_id=${paymentResult.payment_id}${chargeParam}`;
                        navigate(billingUrl, { replace: true });
                    } catch (_e) {
                        // Invalid stored result, just go to billing
                        navigate('/billing', { replace: true });
                    }
                } else {
                    // No stored result, just go to billing
                    navigate('/billing', { replace: true });
                }
            } else {
                // Not authenticated and no payment parameters, redirect to sign-in
                navigate('/sign-in', { replace: true });
            }
        }
    }, [location.search, navigate, isAuthenticated]);

    // Show loading while redirecting
    return (
        <div className="flex flex-col items-center justify-center min-h-screen">
            <FuseLoading />
            <div className="mt-4 text-lg text-gray-600">
                Processing payment result...
            </div>
        </div>
    );
}

export default PaymentCallbackPage;